<template>
  <div>
    <Search
      v-model="searchFraze"
      :focus="searchFocus"
      @search="searchProject" 
      @clearSearch="clearSearch"
      :rounded="true" 
      :shadow="true" 
    />

    <ProjectSelected
      class="rmb100"
      :selected="selected"
      @enableProject="enableProject"
      @enableAllProjects="enableAllProjects"
      @removeSelectedProject="removeSelectedProject"
      @removeAllSelectedProjects="removeAllSelectedProjects"
    />

    <main>
      <ProjectHeader
        :projectsLength="localProjects.length"
        :pagination="pagination"
        :checkedAll="checkedAll"
        @checkAll="checkAll"
      />

      <ProjectsList 
        v-if="localProjects.length" 
        :localProjects="localProjects" 
        class="rmb100"
        @selectProject="selectProject"
      />

      <ResourceState
        v-if="!loading"
        :initiated="initiated"
        :errors="errors"
        :resource-length="localProjects.length"
        :allow-to-load-more="allowToLoadMore"
      />

      <!-- Load More Button -->
      <div
        v-if="!loading"
        class="relative flex items-center"
        :class="[
          pagination.currentPage > 1 ? 'justify-between' : 'justify-center',
        ]"
      >
        <div v-if="allowToLoadMore" class="text-center">
          <BaseBtnLoading
            class="btn-carma"
            :loading="loading"
            @submit="loadMoreProjects"
          />
        </div>
        <div v-if="pagination.currentPage > 1" class="absolute right-0">
          <button class="btn rml100 btn-default" @click="scrollToElement">
            Scroll To Top
          </button>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
// Components
import Search from "../../Search.vue";
import ProjectSelected from "./ProjectSelected.vue";
import ProjectHeader from "./ProjectHeader.vue";
import ProjectsList from "./ProjectsList.vue";
import ResourceState from "./ResourceState.vue";
import BaseBtnLoading from "../../form-components/BaseBtnLoading.vue";

// MIXINS
import { mapGetters } from "vuex";
import loadProjects from "../../../mixins/loaders/loadProjects";
import searchMixin from '../../../mixins/search'

export default {
  mixins: [loadProjects, searchMixin],

  created() {
    this.loadLocalProjects(this.query);
  },
  data() {
    return {
      creating: false,
      selected: [],
      checkedAll: false,
    };
  },
  computed: {
    query() {
      let query = `analysis/projects/all?page=${this.pagination.currentPage +
        1}&per_page=20&order=project_name&sort=asc`;
      if (this.searchFraze) query += `&name=${this.searchFraze}`;
      return query;
    },

    localProjectIds() {
      return this.localProjects.map(p => p.project_id)
    },

    selectedProjectIds() {
      return this.selected.map(p => p.project_id)
    }
  },

  methods: {
    checkAll(e) {
      this.checkedAll = e;
      if (this.checkedAll) return this.selectAllProjects();
      else this.removeAllSelectedProjects();
    },

    loadMoreProjects() {   
      this.loadLocalProjects(this.query)
      .then((projects) => this.testSelectedProjectsIncludeProjects(projects));
    },

    enableProject(project) {
      this.creating = true;
      this.apiCall("analysis/projects/create", "post", {
        project_id: project.project_id,
      })
        .then(() => {
          this.addNewToProjects(project)
          this.$emit('projectAddition')
        })
        .catch((e) => console.error(e))
        .finally(() => (this.creating = false));
    },

    // need an endpoint to consume array of projects to be enabled instead of sending them one by one
    enableAllProjects() {
      this.creating = true;
      this.apiCall("analysis/projects/bulk/create", "post", this.selectedProjectIds)
        .then((data) => {
          this.selected.forEach(project => this.addNewToProjects(project))
          this.resetCheckAll()
          this.$emit('projectAddition')
        })
        .catch((e) => console.error(e))
        .finally(() => (this.creating = false));
    },

    searchProject(searchFraze) {
      this.resetPagination();
      this.resetLocalProjects();
      this.resetCheckAll()
      this.loadLocalProjects(this.query);
    }, 

    addNewToProjects(project) {
      this.removeLocalProjects(project.project_id);
      this.removeSelectedProject(project);
    },

    removeSelectedProject(project) {
      this.selected = this.selected.filter((selectedProject) => {
        if (selectedProject.project_id === project.project_id) {
          this.setSelectLocalProjectState(project, false); // reset isSelected on project
        }
        if (selectedProject.project_id !== project.project_id) {
          return true;
        }
      });
    },

    setSelectLocalProjectState(project, state = false) {
      let targetProject = this.findLocalProject(project.project_id);
      if (targetProject) this.$set(targetProject, 'isSelected', state);
    },

    removeAllSelectedProjects(removeAll=false) {
      // remove all selected and reinsert them back into available projects sorted by cacheIndex
      // if removeAll => remove all selected
      // else remove only if loaded in current localProject array
      this.selected
        .sort((a, b) => b.cachedIndex - a.cachedIndex)
        .forEach((selectedProject) => {
          if(removeAll) return this.removeSelectedProject(selectedProject)
          else if (this.localProjectIds.includes(selectedProject.project_id)) {
            return this.removeSelectedProject(selectedProject)
          }
        });
      this.resetCheckAll()
    },

    resetCheckAll() {
      this.$set(this, 'checkedAll', false)
    },

    clearSearch() {
      this.reinitiate();
      this.resetPagination();
      this.resetLocalProjects();
      this.resetCheckAll();
      this.loadMoreProjects();
      this.setSearchFocus()
    },    

    testSelectedProjectsIncludeProjects(projects) { 
      projects.forEach((project) => {
        if(this.selectedProjectIds.includes(project.project_id)) {
          let p = this.findLocalProject(project.project_id)
          const index = this.findLocalProjectIndex(project.project_id)
          this.setSelectLocalProjectState(p, true)
          this.cacheProjectsIndex(p, index)
        }
      })
    },

    checkIfProjectIsAlreadySelected(project) {
      return this.selectedProjectIds.includes(project.project_id)
    },

    cacheProjectsIndex(project, index) {
      project.cachedIndex = index; // cache index for re-inserting
    },

    selectProject(value, project, index) {
      if (value) {
        if(this.checkIfProjectIsAlreadySelected(project)) return // don't add already selected projects
        this.cacheProjectsIndex(project, index);
        this.setSelectLocalProjectState(project, true);
        this.selected.push(project);
      } else {
        this.removeSelectedProject(project);
      }
    },

    selectAllProjects() {
      this.localProjects.forEach((project, index) =>
        this.selectProject(true, project, index)
      );
    },

    scrollToElement() {
      // Scroll to top button
      const el = document.getElementById("scrollTarget");
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    },
  },

  components: {
    Search,
    ProjectSelected,
    ProjectHeader,
    ProjectsList,
    ResourceState,
    BaseBtnLoading,
  },
};
</script>

<style lang="scss" scoped>
.right-0 {
  right: 0;
}
</style>
